import { useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { lifecycleService } from "../../services/lifecycleService";
import type { Student } from "../../services/studentService";
import { todayIso } from "../../utils/dates";
import { Field } from "../FormLayout";
import { ReasonPanel } from "../inventory/InventoryParts";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { LearnerHistory } from "./LifecycleParts";
import { allowedStandingChanges, learnerConfig, learnerStatus, type StandingChange } from "./lifecycleLogic";

/** Each learner's standing at the school, and the record of every change to it. */
export function LearnersPanel({ rows, canManage, isLoading, isError, onRetry, onChanged, initialView }: {
  rows?: Student[];
  canManage: boolean;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChanged: () => void;
  initialView?: string;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const columns: RegisterColumn<Student>[] = [
    { header: "Learner", cell: (row) => <span className="font-medium text-gray-900">{row.full_name}</span>, exportValue: (row) => row.full_name },
    { header: "Student ID", cell: (row) => row.student_id, exportValue: (row) => row.student_id },
    { header: "Admission no.", cell: (row) => row.admission_number, exportValue: (row) => row.admission_number, exportOnly: true },
    { header: "Class", cell: (row) => row.current_class_name || <span className="text-gray-400">—</span>, exportValue: (row) => row.current_class_name ?? "" },
    { header: "Standing", cell: (row) => <StatusDot {...learnerStatus(row.status)} />, exportValue: (row) => learnerStatus(row.status).label },
  ];
  const row = viewing ? rows?.find((item) => item.id === viewing) : undefined;
  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={initialView}
        title="Learner standing"
        description="Every learner's standing at the school. Open a learner to see their record, or to suspend, restore, withdraw or expel them with the reason kept on file."
        rows={rows}
        config={learnerConfig}
        initialState={initialView ? { view: initialView } : undefined}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => setViewing(item.id)}
        openLabel={(item) => (canManage && allowedStandingChanges(item.status).length ? "Manage" : "Open")}
        noun={["learner", "learners"]}
        exportName={{ file: "learner-standing", title: "Learner Standing" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No learners are on record."
      />
      {row && (
        <LearnerDetails
          row={row}
          canManage={canManage}
          onClose={() => setViewing(null)}
          onDone={(text) => { setViewing(null); setNotice({ tone: "success", text }); onChanged(); }}
        />
      )}
    </div>
  );
}

function LearnerDetails({ row, canManage, onClose, onDone }: { row: Student; canManage: boolean; onClose: () => void; onDone: (text: string) => void }) {
  const options = allowedStandingChanges(row.status);
  const [next, setNext] = useState<StandingChange | "">(options[0]?.value ?? "");
  const [effective, setEffective] = useState(todayIso());
  const chosen = options.find((option) => option.value === next);
  const change = useMutation({
    mutationFn: (reason: string) => lifecycleService.changeStudentStatus(row.id, { status: next as StandingChange, effective_date: effective, reason }),
    onSuccess: (saved) => onDone(`${row.full_name} is now ${learnerStatus(saved.status).label.toLowerCase()}.`),
  });
  return (
    <DetailsModal
      title={row.full_name}
      headline={{ label: "Standing", value: learnerStatus(row.status).label, note: row.current_class_name ? `In ${row.current_class_name}` : "Not in a class" }}
      status={learnerStatus(row.status)}
      facts={[["Student ID", row.student_id], ["Admission no.", row.admission_number], ["Class", row.current_class_name || "—"], ["Admitted", row.admission_date?.slice(0, 10) || "—"]]}
      onClose={onClose}
    >
      {canManage && options.length > 0 && (
        <ReasonPanel
          title="Change their standing"
          description={chosen?.help}
          label="Reason"
          placeholder="The decision and what it rests on"
          context={{ key: "students.lifecycle_note", params: { kind: "status", student: row.id, status: next } }}
          button={chosen ? `${chosen.label}` : "Save"}
          danger={next === "expelled" || next === "withdrawn"}
          busy={change.isPending}
          error={change.error}
          errorText="The change could not be recorded."
          onSubmit={(reason) => (next !== "expelled" || window.confirm(`Expel ${row.full_name}? They leave the school and lose their class place.`)) && change.mutate(reason)}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="New standing">
              <select value={next} onChange={(event) => setNext(event.target.value as StandingChange)} className="input mt-1">
                {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </Field>
            <Field label="From">
              <input type="date" required value={effective} onChange={(event) => setEffective(event.target.value)} className="input mt-1" />
            </Field>
          </div>
        </ReasonPanel>
      )}
      {!options.length && <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">This learner has left the school, so their standing can no longer be changed here.</p>}
      <LearnerHistory studentId={row.id} />
    </DetailsModal>
  );
}
